<script setup>
  import { computed, inject, onMounted, ref } from 'vue'
  import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage'
  import { getApp } from 'firebase/app'
  import BcstProgressBar from './BcstProgressBar.vue'
  import learningIcon from '@/assets/images/bcst-learning-dashboard.svg'
  import checkGreenIcon from '@/assets/images/bcst-check-green.svg'
  import checkGrayIcon from '@/assets/images/bcst-check-gray.svg'
  import { moduleImagePath } from '../../stores/TransformLearningStore'
  import { handleErrors } from '../../utils/errorHandler'

  const props = defineProps({
    module: { type: Object, required: true },
    // Expected shape when progress is wired: { completedCount, percent, status }
    progress: { type: Object, default: null }
  })

  const customNavigation = inject('customNavigation')

  // The store (progressByModule) is the single source of truth for these counts: it
  // derives them from the same enriched category detail every other view reads, so a
  // failed-but-completed quiz is excluded here exactly as it is elsewhere. The module's
  // denormalized lessonCount is only a fallback before progress has loaded.
  const lessonCount = computed(() => props.progress?.lessonCount ?? props.module.lessonCount ?? 0)
  const completedCount = computed(() => props.progress?.completedCount ?? 0)
  const percent = computed(() => Math.round(props.progress?.percent ?? 0))

  const status = computed(() => {
    if (percent.value >= 100 && lessonCount.value > 0) return 'complete'
    if (percent.value > 0) return 'in_progress'
    return 'not_started'
  })

  const ctaLabel = computed(() => {
    if (status.value === 'complete') return 'Review Lesson'
    if (status.value === 'in_progress') return 'Continue Lessons'
    return 'Start Lessons'
  })

  const isComplete = computed(() => status.value === 'complete')

  const imageUrl = ref(null)

  onMounted(async () => {
    if (!props.module.thumbnailFileName || !props.module.courseId) return
    try {
      imageUrl.value = await getDownloadURL(
        storageRef(getStorage(getApp()), moduleImagePath(props.module))
      )
    } catch (err) {
      handleErrors(`TransformModuleCard.resolveImage-${props.module.id}`, err, { sendToSentry: false })
    }
  })

  function goToModule() {
    const route = customNavigation.value?.routes?.transformModuleDetail
    if (!route) return
    customNavigation.value.navigate({
      newPage: route.name,
      params: { moduleId: props.module.id, moduleTitle: props.module.title }
    })
  }
</script>

<template>
  <div class="card tmc-card h-100">
    <div class="card-header bg-light d-flex flex-column align-items-center tmc-card-header">
      <p class="mb-0 tmc-title text-center">{{ module.title }}</p>
    </div>

    <div v-if="imageUrl" class="tmc-banner">
      <img :src="imageUrl" :alt="module.title" class="tmc-banner-img" />
    </div>

    <div class="card-body d-flex flex-column tmc-card-body">
      <BcstProgressBar :value="percent" size="sm" :isComplete="isComplete" class="tmc-progress">
        <template #icon>
          <img
            :src="isComplete ? checkGreenIcon : checkGrayIcon"
            alt=""
            class="flex-shrink-0 tmc-check-icon" />
        </template>
      </BcstProgressBar>

      <!-- Figma: complete + in_progress use text-success; not_started keeps the body color -->
      <div class="d-flex justify-content-between align-items-baseline tmc-status-row">
        <span class="text-16" :class="status !== 'not_started' ? 'text-success' : ''">
          <strong>{{ completedCount }} of {{ lessonCount }} Lessons Complete</strong>
        </span>
        <span class="text-16" :class="status !== 'not_started' ? 'text-success' : ''">
          <med>{{ percent }}%</med>
        </span>
      </div>

      <p v-if="module.description" class="text-14 text-muted flex-grow-1 tmc-desc">
        {{ module.description }}
      </p>
      <div v-else class="flex-grow-1"></div>

      <button
        class="btn w-100"
        :class="isComplete ? 'btn-outline-primary' : 'btn-primary'"
        @click="goToModule">
        {{ ctaLabel }}
        <i class="bi bi-chevron-right ms-2"></i>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* Override Bootstrap card radius to match design spec (4px) */
  .tmc-card {
    border-radius: 4px;
    border-color: var(--bs-gray-300);
  }

  .tmc-card > .card-header:first-child {
    border-radius: 3px 3px 0 0;
    border-bottom: none;
  }

  .tmc-card-header {
    padding: 20px 20px 16px;
  }

  /* White circle contrasts against the gray (bg-light) header background */
  .icon-background {
    background-color: var(--bs-white);
    width: 50px;
    height: 50px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-bottom: 6px;
  }

  .tmc-icon-img {
    width: 28.814px;
    height: 28.814px;
  }

  .tmc-card-body {
    padding: 20px 17px;
  }

  .tmc-title {
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 130%;
    letter-spacing: -0.36px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .tmc-progress {
    margin-bottom: 18px;
  }

  .tmc-status-row {
    margin-bottom: 13px;
  }

  .tmc-desc {
    margin-bottom: 32px;
  }

  .tmc-banner {
    overflow: hidden;
    flex-shrink: 0;
  }

  .tmc-banner-img {
    width: 100%;
    height: 160px;
    object-fit: cover;
    display: block;
  }

  /* Circle center sits on bar's right tip — negative margin = half of icon size (20px).
   z-index raises it above the Bootstrap .progress element.
   SVG icons already include the white background circle. */
  .tmc-check-icon {
    width: 20px;
    height: 20px;
    margin-left: -10px;
    position: relative;
    z-index: 1;
    display: inline-block;
    line-height: 1;
  }
</style>
